import { useState } from 'react';
import { useList, useDelete, useInvalidate } from '@refinedev/core';
import { Plus, Edit, Trash2, Globe, CheckCircle2, XCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { notify } from '@/lib/notify';
import type { CountryItem } from './types';
import { codeToFlagEmoji } from './types';
import { CountryCreateDialog, CountryEditDialog } from './form';
import type { TagItem } from '../tags/types';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

export function CountriesList() {
  useScrollRestoration();
  const [page, setPage] = usePersistedState('countries:page', 1);
  const [search, setSearch] = usePersistedState('countries:search', '');
  const [sort, setSort] = usePersistedState<SortState | undefined>('countries:sort', { field: 'name', order: 'asc' });
  const [deleteTargets, setDeleteTargets] = useState<CountryItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CountryItem | null>(null);
  const pageSize = 50;
  const invalidate = useInvalidate();

  const listResult = useList<CountryItem>({
    resource: 'admin/countries',
    pagination: { mode: 'server', currentPage: page, pageSize },
    filters: search.trim()
      ? [{ field: 'search', operator: 'contains', value: search.trim() }]
      : [],
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const tagsResult = useList<TagItem>({
    resource: 'tags',
    pagination: { pageSize: 500, mode: 'server' },
  });

  const tagsById = new Map<number, TagItem>();
  for (const t of tagsResult.query.data?.data ?? []) {
    tagsById.set(t.id, t);
  }

  const data = listResult.result.data;
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const deleteMutation = useDelete();

  const handleDelete = async () => {
    if (deleteTargets.length === 0) return;
    try {
      await Promise.all(
        deleteTargets.map((target) =>
          deleteMutation.mutateAsync({ resource: 'admin/countries', id: target.id }),
        ),
      );
      setDeleteTargets([]);
      invalidate({ resource: 'admin/countries', invalidates: ['list'] });
    } catch {
      notify.error('Ошибка удаления');
    }
  };

  const columns: Column<CountryItem>[] = [
    {
      key: 'flag',
      header: 'Флаг',
      width: '60px',
      render: (_value, item) => <span className="text-2xl">{codeToFlagEmoji(item.code)}</span>,
    },
    {
      key: 'code',
      header: 'Код',
      sortable: true,
      width: '80px',
      render: (_value, item) => <code className="font-mono text-xs">{item.code}</code>,
    },
    {
      key: 'name',
      header: 'Название',
      sortable: true,
      render: (_value, item) => <span className="font-medium">{item.name}</span>,
    },
    {
      key: 'dialCode',
      header: 'Телефон',
      width: '100px',
      render: (_value, item) => item.dialCode ? <code className="text-xs">{item.dialCode}</code> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'tagIds',
      header: 'Теги',
      render: (_value, item) => (
        <div className="flex flex-wrap gap-1">
          {item.tagIds.length === 0
            ? <span className="text-xs text-muted-foreground">—</span>
            : item.tagIds.map((id) => {
                const tag = tagsById.get(id);
                if (!tag) return null;
                return (
                  <Badge
                    key={id}
                    variant="outline"
                    style={{ borderColor: tag.color, color: tag.color }}
                  >
                    {tag.name}
                  </Badge>
                );
              })}
        </div>
      ),
    },
    {
      key: 'userCount',
      header: 'Пользователи',
      width: '120px',
      render: (_value, item) => (
        <span className="flex items-center gap-1 text-sm text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          {item.userCount}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      width: '100px',
      render: (_value, item) => item.isActive
        ? <Badge variant="outline" className="border-emerald-500/40 text-emerald-600"><CheckCircle2 className="mr-1 h-3 w-3" />Активна</Badge>
        : <Badge variant="outline" className="border-muted text-muted-foreground"><XCircle className="mr-1 h-3 w-3" />Отключена</Badge>,
    },
  ];

  const bulkActions: BulkAction<CountryItem>[] = [
    {
      label: 'Редактировать',
      icon: <Edit className="h-4 w-4" />,
      showOnlyWhenSelected: true,
      onClick: (countries) => {
        if (countries.length !== 1) {
          notify.info('Для редактирования выберите одну страну');
          return;
        }
        setEditTarget(countries[0]);
      },
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      showOnlyWhenSelected: true,
      onClick: setDeleteTargets,
    },
  ];

  return (
    <div className="space-y-4">
      <FormHeader
        title="Страны"
        description="Справочник стран для регистрации/профиля и авто-тегирования пользователей"
        icon={<Globe className="h-5 w-5" />}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить страну
          </Button>
        }
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(value) => { setSearch(value); setPage(1); }}
        searchPlaceholder="Поиск по названию или коду..."
      />

      <DataTable
        data={data}
        columns={columns}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        sort={sort}
        onSortChange={(next) => setSort(next ?? undefined)}
        selectable
        bulkActions={bulkActions}
        onRowClick={setEditTarget}
        rowKey="id"
        emptyMessage="Страны не созданы. Нажмите «Добавить страну» чтобы начать."
      />

      <AlertDialog open={deleteTargets.length > 0} onOpenChange={(open) => !open && setDeleteTargets([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTargets.length === 1 ? 'Удалить страну?' : `Удалить стран: ${deleteTargets.length}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTargets.length === 1
                ? `Страна "${deleteTargets[0].name}" будет удалена. У ${deleteTargets[0].userCount} пользователей countryId будет сброшен в NULL. Ранее назначенные авто-теги останутся (не очищаются исторически).`
                : 'Выбранные страны будут удалены. У связанных пользователей countryId будет сброшен в NULL. Ранее назначенные авто-теги останутся (не очищаются исторически).'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Удалить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CountryCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CountryEditDialog country={editTarget} onClose={() => setEditTarget(null)} />
    </div>
  );
}
